import { REF, over, under, phDeviation, round2 } from "./scale";
import type { Averages, Ranked } from "./source-profiles";

const PROFILES = [
  {
    key: "textile_dyeing",
    label: "Textile / Dyeing",
    explain: "High COD + Color + EC/Chloride; pH swings",
    score: (x: Averages) =>
      0.35 * over(x.cod_mg_l, REF.cod_mg_l) +
      0.25 * over(x.color_hazen, REF.color_hazen) +
      0.20 * over(x.ec_us_cm, REF.ec_us_cm) +
      0.15 * over(x.chloride_mg_l, REF.chloride_mg_l) +
      0.05 * phDeviation(x.ph)
  },
  {
    key: "tannery",
    label: "Tannery / Leather",
    explain: "COD + Chloride/EC; alkaline/acidic steps",
    score: (x: Averages) =>
      0.35 * over(x.cod_mg_l, REF.cod_mg_l) +
      0.25 * over(x.chloride_mg_l, REF.chloride_mg_l) +
      0.25 * over(x.ec_us_cm, REF.ec_us_cm) +
      0.15 * phDeviation(x.ph)
  },
  {
    key: "food_processing",
    label: "Food / Sugar / Dairy",
    explain: "Very high BOD/COD, warm, DO depressed",
    score: (x: Averages) =>
      0.35 * over(x.bod_mg_l, REF.bod_mg_l) +
      0.30 * over(x.cod_mg_l, REF.cod_mg_l) +
      0.20 * under(x.do_mg_l, REF.do_mg_l) +
      0.15 * over(x.temp_c, REF.temp_c)
  },
  {
    key: "pulp_paper",
    label: "Pulp & Paper",
    explain: "COD + Color + TOC/DOC; often alkaline",
    score: (x: Averages) =>
      0.35 * over(x.cod_mg_l, REF.cod_mg_l) +
      0.25 * over(x.color_hazen, REF.color_hazen) +
      0.25 * Math.max(over(x.toc_mg_l, REF.toc_mg_l), over(x.doc_mg_l, REF.doc_mg_l)) +
      0.15 * phDeviation(x.ph)
  },
  {
    key: "refinery_oilgas",
    label: "Refinery / Oil & Gas",
    explain: "COD + TOC/DOC; DO depressed",
    score: (x: Averages) =>
      0.40 * over(x.cod_mg_l, REF.cod_mg_l) +
      0.30 * Math.max(over(x.toc_mg_l, REF.toc_mg_l), over(x.doc_mg_l, REF.doc_mg_l)) +
      0.30 * under(x.do_mg_l, REF.do_mg_l)
  },
  {
    key: "power_thermal",
    label: "Power / Thermal",
    explain: "Temperature ↑ with DO ↓",
    score: (x: Averages) =>
      0.60 * over(x.temp_c, REF.temp_c) +
      0.40 * under(x.do_mg_l, REF.do_mg_l)
  },
  {
    key: "construction_stone",
    label: "Construction / Stone",
    explain: "High TSS/Turbidity spikes",
    score: (x: Averages) =>
      0.50 * over(x.tss_mg_l, REF.tss_mg_l) +
      0.50 * over(x.turbidity_ntu, REF.turbidity_ntu)
  },
  {
    key: "agriculture",
    label: "Agriculture",
    explain: "NH₃ + TSS/Turbidity + TOC/DOC; EC moderate",
    score: (x: Averages) =>
      0.35 * over(x.nh3_mg_l, REF.nh3_mg_l) +
      0.25 * Math.max(over(x.tss_mg_l, REF.tss_mg_l), over(x.turbidity_ntu, REF.turbidity_ntu)) +
      0.25 * Math.max(over(x.toc_mg_l, REF.toc_mg_l), over(x.doc_mg_l, REF.doc_mg_l)) +
      0.15 * Math.min(over(x.ec_us_cm, REF.ec_us_cm), 0.6)
  }
];

export function rankSources(avg: Averages): Ranked[] {
  const rows = PROFILES.map(p => ({
    key: p.key,
    label: p.label,
    explain: p.explain,
    score: round2(p.score(avg)),
  }));

  const max = Math.max(0, ...rows.map(r => r.score));
  // If everything is zero -> no signal; return empty to let UI show empty state
  if (max === 0) return [];

  // Normalize to 0..1 by top score
  rows.forEach(r => (r.score = round2(r.score / (max || 1))));
  return rows.sort((a, b) => b.score - a.score);
}
